/**
 * Technical Approach Repository
 * Methodology library for proposals with reusable technical narratives, innovation showcase, and risk mitigation strategies
 * Entity Type: technical_approach
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, EmptyState, Select } from '@tracekit/core';
import { useWorkspace, queryEntities } from '@tracekit/core';
import {
  Search,
  Filter,
  Download,
  Plus,
  Edit2,
  ChevronDown,
  ChevronRight,
  Award,
  FileText,
  Clock,
  Users,
  CheckCircle2,
  AlertTriangle,
  Shield,
  Lightbulb,
  Target,
  Clipboard,
} from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useToastStore } from '@tracekit/core';
import { useWorkplanGeneration } from '../contexts/WorkplanGenerationContext'; // NEW: Phase 4
import { TechnicalApproachCreationModal } from '../components/TechnicalApproachCreationModal';

// Entity type from Policy Pack v2.0
interface TechnicalApproachEntity {
  id: string;
  entity_type: string;
  data: {
    approach_id?: string;
    artifact_id?: string;
    approach_name: string;
    service_line?: string;
    project_type?: string;
    methodology_summary?: string;
    key_steps?: string[];
    tools_technologies?: string[];
    standards_followed?: string[];
    innovations?: string[];
    success_metrics?: string[];
    typical_duration?: string;
    resource_requirements?: string[];
    quality_assurance?: string;
    risk_mitigation?: string[];
    environmental_considerations?: string;
    safety_protocols?: string[];
    client_engagement?: string;
  };
  created_at?: string;
  updated_at?: string;
}

interface FilterState {
  serviceLine: string;
  projectType: string;
  hasInnovations: boolean;
}

export function TechnicalApproachesPage() {
  const { projectId } = useWorkspace();
  const { addToast } = useToastStore();
  const { selectedApproaches: contextApproaches, addApproach, removeApproach } = useWorkplanGeneration(); // NEW: Phase 4
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false); // NEW: Phase 4 - toggle selection mode for workplan generation
  const [localSelected, setLocalSelected] = useState<Set<string>>(new Set()); // NEW: Phase 4 - local selection state
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState<FilterState>({
    serviceLine: 'all',
    projectType: '',
    hasInnovations: false,
  });

  // Fetch technical approach entities with pagination
  const { data: approachesData, isLoading, error } = useQuery({
    queryKey: ['technical-approach-entities', projectId, page, pageSize],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'technical_approach',
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      return {
        entities: result.entities.map(e => ({
          id: e.id,
          entity_type: e.entity_type || 'technical_approach',
          data: (e.data || {}) as TechnicalApproachEntity['data'],
          created_at: e.created_at || new Date().toISOString(),
          updated_at: e.updated_at,
        })) as TechnicalApproachEntity[],
        total: result.total_count,
        hasMore: result.pagination.has_more,
      };
    },
    enabled: !!projectId,
  });

  const approaches = approachesData?.entities || [];
  const totalCount = approachesData?.total || 0;
  const hasMore = approachesData?.hasMore || false;

  // Filtered approaches
  const filteredApproaches = useMemo(() => {
    if (!approaches) return [];
    
    return approaches.filter((approach) => {
      // Search query
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          approach.data.approach_name?.toLowerCase().includes(searchLower) ||
          approach.data.methodology_summary?.toLowerCase().includes(searchLower) ||
          approach.data.project_type?.toLowerCase().includes(searchLower) ||
          approach.data.tools_technologies?.some(t => t.toLowerCase().includes(searchLower)) ||
          approach.data.innovations?.some(i => i.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }
      
      // Service line filter
      if (filters.serviceLine !== 'all' && approach.data.service_line !== filters.serviceLine) {
        return false;
      }
      
      // Project type filter
      if (filters.projectType && !approach.data.project_type?.toLowerCase().includes(filters.projectType.toLowerCase())) {
        return false;
      }
      
      // Has innovations filter
      if (filters.hasInnovations && (!approach.data.innovations || approach.data.innovations.length === 0)) {
        return false;
      }
      
      return true;
    });
  }, [approaches, searchQuery, filters]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!filteredApproaches) return {
      total: 0,
      withInnovations: 0,
      withRiskMitigation: 0,
      avgSteps: 0,
    };
    
    const total = filteredApproaches.length;
    const withInnovations = filteredApproaches.filter(a => a.data.innovations && a.data.innovations.length > 0).length;
    const withRiskMitigation = filteredApproaches.filter(a => a.data.risk_mitigation && a.data.risk_mitigation.length > 0).length;
    
    const steps = filteredApproaches.filter(a => a.data.key_steps).map(a => a.data.key_steps!.length);
    const avgSteps = steps.length > 0 ? steps.reduce((a, b) => a + b, 0) / steps.length : 0;
    
    return { total, withInnovations, withRiskMitigation, avgSteps };
  }, [filteredApproaches]);

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleClearFilters = () => {
    setFilters({
      serviceLine: 'all',
      projectType: '',
      hasInnovations: false,
    });
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (format === 'csv') {
      const headers = [
        'Approach Name', 'Service Line', 'Project Type', 'Methodology Summary', 
        'Key Steps', 'Tools/Technologies', 'Standards', 'Innovations', 'Typical Duration'
      ];
      const rows = filteredApproaches.map((approach) => [
        approach.data.approach_name || 'N/A',
        approach.data.service_line || 'N/A',
        approach.data.project_type || 'N/A',
        approach.data.methodology_summary || 'N/A',
        approach.data.key_steps?.length.toString() || '0',
        approach.data.tools_technologies?.length.toString() || '0',
        approach.data.standards_followed?.length.toString() || '0',
        approach.data.innovations?.length.toString() || '0',
        approach.data.typical_duration || 'N/A',
      ]);
      const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `technical-approaches-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([JSON.stringify(filteredApproaches, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `technical-approaches-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleAddToProposal = (approach: TechnicalApproachEntity) => {
    addToast({
      type: 'success',
      title: 'Added to proposal',
      message: `${approach.data.approach_name} added to proposal template`,
    });
  };

  // NEW: Phase 4 - Selection mode handlers
  const toggleSelection = (approach: TechnicalApproachEntity) => {
    setLocalSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(approach.id)) {
        newSet.delete(approach.id);
      } else {
        newSet.add(approach.id);
      }
      return newSet;
    });
  };

  const handleConfirmSelection = () => {
    // Add all selected approaches to the context
    localSelected.forEach(id => {
      const approach = approaches.find(a => a.id === id);
      if (approach) {
        addApproach(approach);
      }
    });
    
    addToast({
      type: 'success',
      title: 'Approaches selected',
      message: `${localSelected.size} technical approach(es) selected for workplan generation`,
    });
    
    setSelectionMode(false);
    setLocalSelected(new Set());
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setLocalSelected(new Set());
  };

  if (!projectId) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">Please select a project to view technical approaches.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Breadcrumbs items={[
        { label: 'RFP Proposals', path: '/rfp/proposals' },
        { label: 'Technical Approaches' },
      ]} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Lightbulb className="w-8 h-8" />
          Technical Approach Repository
        </h1>
        <p className="text-gray-600 mt-1">
          Reusable methodology library with technical narratives, innovations, and risk mitigation strategies
        </p>
      </div>

      {/* Stats Cards */}
      {approaches && approaches.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-sm text-gray-600">Total Approaches</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">With Innovations</div>
            <div className="text-2xl font-bold text-blue-600">{stats.withInnovations}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">With Risk Mitigation</div>
            <div className="text-2xl font-bold text-green-600">{stats.withRiskMitigation}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-gray-600">Avg Steps</div>
            <div className="text-2xl font-bold text-purple-600">{stats.avgSteps.toFixed(1)}</div>
          </Card>
        </div>
      )}

      {/* Search & Filters */}
      <Card className="mb-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search approaches by name, methodology, tools, or innovations..."
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {Object.values(filters).filter(v => v && v !== 'all' && v !== false).length > 0 && (
                <Badge variant="default" className="ml-2">
                  {Object.values(filters).filter(v => v && v !== 'all' && v !== false).length}
                </Badge>
              )}
            </Button>
            {/* NEW: Phase 4 - Selection mode for workplan generation */}
            {!selectionMode ? (
              <Button 
                variant="outline"
                onClick={() => setSelectionMode(true)}
              >
                <Clipboard className="h-4 w-4 mr-2" />
                Select for Workplan
              </Button>
            ) : (
              <>
                <Button 
                  variant="default"
                  onClick={handleConfirmSelection}
                  disabled={localSelected.size === 0}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Use {localSelected.size} Selected
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleCancelSelection}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Service Line</label>
                  <Select
                    value={filters.serviceLine}
                    onChange={(e) => setFilters({ ...filters, serviceLine: e.target.value })}
                  >
                    <option value="all">All</option>
                    <option value="Contaminated Sites Assessment & Remediation">Contaminated Sites</option>
                    <option value="Environmental Engineering">Environmental Engineering</option>
                    <option value="Biology & Environmental Impact Assessment">Biology/EIA</option>
                    <option value="Indoor Air Quality & Hazardous Materials">IAQ/HazMat</option>
                    <option value="EHS Management & Compliance">EHS Management</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project Type</label>
                  <Input
                    value={filters.projectType}
                    onChange={(e) => setFilters({ ...filters, projectType: e.target.value })}
                    placeholder="Filter by project type"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filters.hasInnovations}
                      onChange={(e) => setFilters({ ...filters, hasInnovations: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Has Innovations</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear All
                </Button>
                <Button onClick={() => setShowFilters(false)}>Apply Filters</Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Action Bar */}
      {approaches && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount} technical approaches
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            <Button 
              size="sm"
              onClick={() => setShowCreationModal(true)}
              title="Create a new technical approach from categories"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Approach
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Error loading technical approaches: {error instanceof Error ? error.message : 'Unknown error'}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && (!approaches || approaches.length === 0) && (
        <Card>
          <EmptyState
            icon={<Lightbulb className="w-12 h-12" />}
            title="No Technical Approaches Found"
            description="No technical approach records found in the database. Add methodologies to build your reusable approach library."
          />
        </Card>
      )}

      {/* Approaches List */}
      {!isLoading && approaches && approaches.length > 0 && (
        <>
          {filteredApproaches.length === 0 ? (
            <Card>
              <div className="text-center py-12 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No technical approaches found matching your search</p>
                <p className="text-sm mt-2">Try adjusting your filters or search query</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredApproaches.map((approach) => {
                const isExpanded = expandedRows.has(approach.id);
                const isSelected = selectionMode && localSelected.has(approach.id); // NEW: Phase 4 - use localSelected in selection mode

                return (
                  <Card key={approach.id} className={isSelected ? 'ring-2 ring-blue-500' : ''}>
                    <div
                      className="cursor-pointer p-4"
                      onClick={() => toggleRow(approach.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold">{approach.data.approach_name}</h3>
                              {approach.data.service_line && (
                                <Badge variant="outline">{approach.data.service_line}</Badge>
                              )}
                              {approach.data.innovations && approach.data.innovations.length > 0 && (
                                <Badge variant="default" className="bg-blue-600">
                                  <Lightbulb className="w-3 h-3 mr-1" />
                                  {approach.data.innovations.length} Innovations
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-600 mt-1 line-clamp-2">{approach.data.methodology_summary}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              {approach.data.project_type && (
                                <span className="flex items-center">
                                  <FileText className="w-4 h-4 mr-1" />
                                  {approach.data.project_type}
                                </span>
                              )}
                              {approach.data.typical_duration && (
                                <span className="flex items-center">
                                  <Clock className="w-4 h-4 mr-1" />
                                  {approach.data.typical_duration}
                                </span>
                              )}
                              {approach.data.key_steps && (
                                <span className="flex items-center">
                                  <Target className="w-4 h-4 mr-1" />
                                  {approach.data.key_steps.length} steps
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* NEW: Phase 4 - Only show checkbox in selection mode */}
                        {selectionMode && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelection(approach);
                              }}
                              className="ml-2 w-5 h-5 cursor-pointer"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t p-4 space-y-4">
                        {/* Methodology Summary */}
                        {approach.data.methodology_summary && (
                          <div>
                            <h4 className="font-medium mb-2">Methodology Summary</h4>
                            <p className="text-gray-700">{approach.data.methodology_summary}</p>
                          </div>
                        )}
                        
                        {/* Key Steps */}
                        {approach.data.key_steps && approach.data.key_steps.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <Clipboard className="w-4 h-4 mr-2" />
                              Key Steps ({approach.data.key_steps.length})
                            </h4>
                            <ol className="list-decimal pl-5 space-y-1">
                              {approach.data.key_steps.map((step, idx) => (
                                <li key={idx} className="text-gray-700">{step}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        
                        {/* Tools & Technologies */}
                        {approach.data.tools_technologies && approach.data.tools_technologies.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Tools & Technologies</h4>
                            <div className="flex flex-wrap gap-2">
                              {approach.data.tools_technologies.map((tool, idx) => (
                                <Badge key={idx} variant="secondary">{tool}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Standards Followed */}
                        {approach.data.standards_followed && approach.data.standards_followed.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <Award className="w-4 h-4 mr-2" />
                              Standards Followed
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {approach.data.standards_followed.map((standard, idx) => (
                                <Badge key={idx} variant="outline">{standard}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Innovations */}
                        {approach.data.innovations && approach.data.innovations.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <Lightbulb className="w-4 h-4 mr-2" />
                              Innovations ({approach.data.innovations.length})
                            </h4>
                            <ul className="list-disc pl-4 space-y-1">
                              {approach.data.innovations.map((innovation, idx) => (
                                <li key={idx} className="text-blue-700">{innovation}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Success Metrics */}
                        {approach.data.success_metrics && approach.data.success_metrics.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Success Metrics
                            </h4>
                            <ul className="list-disc pl-4 space-y-1">
                              {approach.data.success_metrics.map((metric, idx) => (
                                <li key={idx} className="text-green-700">{metric}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Risk Mitigation */}
                        {approach.data.risk_mitigation && approach.data.risk_mitigation.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <AlertTriangle className="w-4 h-4 mr-2" />
                              Risk Mitigation Strategies
                            </h4>
                            <ul className="list-disc pl-4 space-y-1">
                              {approach.data.risk_mitigation.map((risk, idx) => (
                                <li key={idx} className="text-yellow-700">{risk}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Resource Requirements */}
                        {approach.data.resource_requirements && approach.data.resource_requirements.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              Resource Requirements
                            </h4>
                            <ul className="list-disc pl-4 space-y-1 text-sm">
                              {approach.data.resource_requirements.map((resource, idx) => (
                                <li key={idx} className="text-gray-700">{resource}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Quality Assurance & Safety */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {approach.data.quality_assurance && (
                            <div>
                              <h4 className="font-medium mb-2 flex items-center">
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Quality Assurance
                              </h4>
                              <p className="text-sm text-gray-700">{approach.data.quality_assurance}</p>
                            </div>
                          )}
                          
                          {approach.data.safety_protocols && approach.data.safety_protocols.length > 0 && (
                            <div>
                              <h4 className="font-medium mb-2 flex items-center">
                                <Shield className="w-4 h-4 mr-2" />
                                Safety Protocols
                              </h4>
                              <ul className="list-disc pl-4 space-y-1 text-sm">
                                {approach.data.safety_protocols.map((protocol, idx) => (
                                  <li key={idx} className="text-gray-700">{protocol}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        {/* Environmental & Client Engagement */}
                        {(approach.data.environmental_considerations || approach.data.client_engagement) && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {approach.data.environmental_considerations && (
                              <div>
                                <h4 className="font-medium mb-2">Environmental Considerations</h4>
                                <p className="text-sm text-gray-700">{approach.data.environmental_considerations}</p>
                              </div>
                            )}
                            
                            {approach.data.client_engagement && (
                              <div>
                                <h4 className="font-medium mb-2">Client Engagement</h4>
                                <p className="text-sm text-gray-700">{approach.data.client_engagement}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToProposal(approach);
                            }}
                            className="flex-1"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add to Proposal
                          </Button>
                          <Button variant="outline" size="sm">
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-2" />
                            Export
                          </Button>
                        </div>
                        
                        <div className="text-xs text-gray-400 pt-2 border-t">
                          ID: {approach.id} | Created: {approach.created_at ? new Date(approach.created_at).toLocaleDateString() : 'N/A'}
                          {approach.updated_at && ` | Updated: ${new Date(approach.updated_at).toLocaleDateString()}`}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {filteredApproaches.length > 0 && (
            <Card className="p-4 mt-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, Math.ceil(totalCount / pageSize)) }, (_, i) => i + 1).map(pageNum => (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'default' : 'outline'}
                      onClick={() => setPage(pageNum)}
                      className="w-10"
                    >
                      {pageNum}
                    </Button>
                  ))}
                  {Math.ceil(totalCount / pageSize) > 5 && <span className="text-gray-500">...</span>}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                >
                  Next
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
      
      {/* Creation Modal */}
      {showCreationModal && (
        <TechnicalApproachCreationModal onClose={() => setShowCreationModal(false)} />
      )}
    </div>
  );
}
